import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatBirthDate } from '@/util/date.ts';
import { useAppLocale } from '@/locales/locale';
import { MentalHealthReport } from '@/api/mentalHealth/report.types';
import { IUser } from '@/api/user/getUsers';
import { calculateAge } from '@/util/person';
import VitalSignsChart from '@/component/mentalHealth/VitalSignsChart';
import { SymptomPredictionCard } from '@/component/mentalHealth/SymptomPredictionCard';
import { formatRounded, numberOr } from '@/util/mentalHealthFormat';

interface MentalHealthReportDetailedPDFTemplateProps {
    report: MentalHealthReport;
    user?: IUser;
    analysisDate?: string;
}

const MentalHealthReportDetailedPDFTemplate: React.FC<MentalHealthReportDetailedPDFTemplateProps> = ({ report, user, analysisDate }) => {
    const { t } = useTranslation();
    const { intlLocale } = useAppLocale();

    // Helper to translate values
    const translateValue = (value: string | undefined, prefix?: string): string => {
        if (!value) return '';
        let translationKey: string;
        if (value.startsWith('mentalHealth.')) {
            translationKey = value;
        } else if (prefix) {
            translationKey = `${prefix}.${value}`;
        } else {
            return value;
        }
        const translated = t(translationKey);
        return translated === translationKey ? value : translated;
    };

    const genderName = (gender: number) => {
        if (gender === 0) return t('users.list.item.gender.male');
        if (gender === 1) return t('users.list.item.gender.female');
        return t('users.list.item.gender.none');
    };

    const formatDate = (dateStr?: string) => {
        const date = dateStr ? new Date(dateStr) : new Date();
        return date.toLocaleDateString(intlLocale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="bg-white text-black w-[1000px] mx-auto" id="pdf-template-detailed">
            {/* ===== 페이지 1: 개인정보 ===== */}
            <div className="p-8">
                {/* 주의사항 배너 */}
                <div data-pdf-block className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-amber-800">
                        <span className="font-bold">⚠️ {t('mentalHealth.reportDetail.importantNotice')}:</span> {t('mentalHealth.reportDetail.disclaimerText')}
                    </p>
                </div>

                {/* Header */}
                <div data-pdf-block className="border-b-2 border-gray-800 pb-4 mb-8 flex justify-between items-end">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{t('mentalHealth.reportDetail.title')}</h1>
                        <p className="text-gray-600 mt-1">{t('mentalHealth.reportDetail.subtitle')}</p>
                    </div>
                    <div className="text-right">
                        <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.analysisDate')}</div>
                        <div className="text-lg font-semibold text-gray-700">
                            {formatDate(analysisDate)}
                        </div>
                    </div>
                </div>

                {/* User Info Section - 개인정보 */}
                {user && (
                    <div data-pdf-block className="bg-gray-50 rounded-xl border border-gray-200 p-6 mb-8">
                        <h2 data-pdf-heading className="text-2xl font-bold text-gray-800 mb-6 border-l-4 border-blue-600 pl-3">{t('users.detail.basicInfo')}</h2>
                        <div className="grid grid-cols-2 gap-8">
                            {/* Left Column: Basic Info & Contact */}
                            <div className="space-y-6">
                                <div>
                                    <h3 className="font-semibold text-gray-700 mb-2">{t('users.detail.info')}</h3>
                                    <div className="bg-white p-4 rounded-lg border border-gray-100 space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{t('users.detail.name')}</span>
                                            <span className="font-medium text-lg">{user.name}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{t('users.detail.birthDate')}</span>
                                            <span className="font-medium">
                                                {formatBirthDate(user.birthDate)} ({t('users.list.item.age', { count: calculateAge(user.birthDate) })})
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{t('users.detail.gender')}</span>
                                            <span className="font-medium">{genderName(user.gender)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{t('users.detail.address')}</span>
                                            <span className="font-medium text-right max-w-[250px]">{user.address || '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-semibold text-gray-700 mb-2">{t('users.detail.contact')}</h3>
                                    <div className="bg-white p-4 rounded-lg border border-gray-100 space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{t('users.detail.contact.self')}</span>
                                            <span className="font-medium">{user.contact || '-'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{user.primaryRelation || t('users.detail.contact.guardian')}</span>
                                            <span className="font-medium">{user.primaryContact || '-'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: Notes/Caution */}
                            <div>
                                <h3 className="font-semibold text-gray-700 mb-2">{t('users.detail.memo')}</h3>
                                <div className="bg-white p-4 rounded-lg border border-gray-100 h-full min-h-[200px]">
                                    <p className="text-gray-700 whitespace-pre-wrap">
                                        {user.inspectorMemo || t('common.noData')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Quick Summary */}
                <div data-pdf-block className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl border border-blue-200 p-6">
                    <h2 data-pdf-heading className="text-xl font-bold text-gray-800 mb-4">📊 {t('mentalHealth.detail.comprehensiveHealthAssessment')}</h2>
                    <div className="grid grid-cols-4 gap-4">
                        <div className="bg-white/80 rounded-lg p-4 text-center">
                            <div className={`text-4xl font-black mb-2 ${numberOr(report.mindScore.score) >= 80 ? 'text-emerald-600' :
                                numberOr(report.mindScore.score) >= 70 ? 'text-blue-600' :
                                    numberOr(report.mindScore.score) >= 55 ? 'text-amber-600' : 'text-red-600'
                                }`}>
                                {formatRounded(report.mindScore.score)}
                            </div>
                            <div className="text-sm text-gray-600">{t('mentalHealth.detail.mindHealthScore')}</div>
                        </div>
                        <div className="bg-white/80 rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-gray-800 mb-2">
                                {t(`mentalHealth.status.${report.decisionLabel}`)}
                            </div>
                            <div className="text-sm text-gray-600">{t('mentalHealth.detail.statusLabel')}</div>
                        </div>
                        <div className="bg-white/80 rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-gray-800 mb-2">
                                {t(`mentalHealth.stress.${report.stressAnalysis.category}`)}
                            </div>
                            <div className="text-sm text-gray-600">{t('mentalHealth.detail.stressLabel')}</div>
                        </div>
                        <div className="bg-white/80 rounded-lg p-4 text-center">
                            <div className="text-2xl font-bold text-gray-800 mb-2">
                                {((report.qualityMetrics?.analyzability || 0) * 100).toFixed(0)}%
                            </div>
                            <div className="text-sm text-gray-600">{t('mentalHealth.detail.dataQualityLabel')}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== 페이지 2: 개요 (Overview) ===== */}
            <div className="p-8">
                <h2 data-pdf-heading className="text-2xl font-bold border-l-4 border-blue-600 pl-4 mb-6">📊 {t('mentalHealth.detail.overview')}</h2>

                {/* 종합 건강 상태 */}
                <div data-pdf-block className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 rounded-xl p-6 border border-slate-200 mb-6">
                    <div className="grid grid-cols-3 gap-6">
                        {/* Mind Score */}
                        <div className="flex flex-col items-center justify-center p-4 bg-white/70 rounded-xl">
                            <div className="text-lg font-bold text-gray-700 mb-2">{t('mentalHealth.detail.mindHealthScore')}</div>
                            <div className={`text-5xl font-black mb-2 ${numberOr(report.mindScore.score) >= 80 ? 'text-emerald-600' :
                                numberOr(report.mindScore.score) >= 70 ? 'text-blue-600' :
                                    numberOr(report.mindScore.score) >= 55 ? 'text-amber-600' : 'text-red-600'
                                }`}>
                                {formatRounded(report.mindScore.score)}
                            </div>
                            <div className={`px-4 py-1 rounded-full text-sm font-bold text-white ${report.decisionLabel === 'healthy' ? 'bg-emerald-500' :
                                report.decisionLabel === 'normal' ? 'bg-blue-500' :
                                    report.decisionLabel === 'caution' ? 'bg-amber-500' : 'bg-red-500'
                                }`}>
                                {t(`mentalHealth.status.${report.decisionLabel}`)}
                            </div>
                        </div>

                        {/* Key Metrics */}
                        <div className="col-span-2 grid grid-cols-2 gap-3">
                            {report.mindScore.components && (
                                <>
                                    <div className="bg-white/70 p-3 rounded-lg border border-gray-200">
                                        <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.heartRate')}</div>
                                        <div className="text-xl font-bold">{formatRounded(report.mindScore.components.HR_med)} <span className="text-sm font-normal text-gray-500">bpm</span></div>
                                    </div>
                                    <div className="bg-white/70 p-3 rounded-lg border border-gray-200">
                                        <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.rmssd')}</div>
                                        <div className="text-xl font-bold">{report.mindScore.components?.RMSSD_med?.toFixed(1)} <span className="text-sm font-normal text-gray-500">ms</span></div>
                                    </div>
                                    <div className="bg-white/70 p-3 rounded-lg border border-gray-200">
                                        <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.stressLevel')}</div>
                                        <div className="text-lg font-bold">{t(`mentalHealth.stress.${report.stressAnalysis.category}`)}</div>
                                    </div>
                                    <div className="bg-white/70 p-3 rounded-lg border border-gray-200">
                                        <div className="text-sm text-gray-500">{t('mentalHealth.reportDetail.balance')}</div>
                                        <div className="text-lg font-bold">{translateValue(report.balanceAnalysis.flag, 'mentalHealth.balance')}</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Interpretation */}
                <div data-pdf-block className="bg-blue-50 p-6 rounded-xl border border-blue-100 mb-6">
                    <h3 className="font-bold text-blue-900 mb-3">👨‍⚕️ {t('mentalHealth.reportDetail.interpretation')}</h3>
                    <p className="text-blue-800 mb-2">{translateValue(report.stressAnalysis.description)}</p>
                    <p className="text-blue-800">{translateValue(report.balanceAnalysis.description)}</p>
                </div>

                {/* 자율신경 활성도 */}
                <div data-pdf-block className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{t('mentalHealth.detail.ansActivity')}</h3>
                    <div className="grid grid-cols-2 gap-6">
                        {/* HRV Power Chart */}
                        <div className="flex justify-center items-end space-x-4 h-32">
                            <div className="flex flex-col items-center">
                                <div className="bg-blue-500 rounded-t-lg w-12 flex items-end justify-center text-white font-bold text-xs pb-1"
                                    style={{ height: `${Math.min((numberOr(report.psdData.TP) / 3000) * 100, 100)}%` }}>
                                    {Math.round(numberOr(report.psdData.TP) / 100)}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">TP</div>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="bg-yellow-500 rounded-t-lg w-12 flex items-end justify-center text-white font-bold text-xs pb-1"
                                    style={{ height: `${Math.min((numberOr(report.psdData.VLF) / 1000) * 100, 100)}%` }}>
                                    {Math.round(numberOr(report.psdData.VLF) / 10)}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">VLF</div>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="bg-red-500 rounded-t-lg w-12 flex items-end justify-center text-white font-bold text-xs pb-1"
                                    style={{ height: `${Math.min((numberOr(report.psdData.LF) / 1000) * 100, 100)}%` }}>
                                    {Math.round(numberOr(report.psdData.LF) / 10)}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">LF</div>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="bg-green-500 rounded-t-lg w-12 flex items-end justify-center text-white font-bold text-xs pb-1"
                                    style={{ height: `${Math.min((numberOr(report.psdData.HF) / 1000) * 100, 100)}%` }}>
                                    {Math.round(numberOr(report.psdData.HF) / 10)}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">HF</div>
                            </div>
                        </div>

                        {/* Detailed Values */}
                        <div className="space-y-2">
                            <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
                                <span className="text-sm text-gray-600">{t('mentalHealth.detail.totalPower')}</span>
                                <span className="font-bold">{report.psdData?.TP?.toFixed(0)} ms²</span>
                            </div>
                            <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
                                <span className="text-sm text-gray-600">{t('mentalHealth.detail.lfhfRatio')}</span>
                                <span className="font-bold">{report.psdData?.LF_HF?.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
                                <span className="text-sm text-gray-600">{t('mentalHealth.detail.normalizedLF')}</span>
                                <span className="font-bold">{report.psdData?.LFnu?.toFixed(1)}%</span>
                            </div>
                            <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
                                <span className="text-sm text-gray-600">{t('mentalHealth.detail.normalizedHF')}</span>
                                <span className="font-bold">{report.psdData?.HFnu?.toFixed(1)}%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SNS/PNS Balance */}
                <div data-pdf-block className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{t('mentalHealth.detail.ansBalance')}</h3>
                    <div className="space-y-4">
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-sm font-medium text-gray-700">SNS ({t('mentalHealth.detail.sympatheticLabel')})</span>
                                <span className="font-bold text-pink-600">{report.psdData?.LFnu?.toFixed(1)}%</span>
                            </div>
                            <div className="bg-gray-200 rounded-full h-4">
                                <div className="bg-gradient-to-r from-pink-400 to-pink-600 h-4 rounded-full"
                                    style={{ width: `${report.psdData.LFnu}%` }}></div>
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-sm font-medium text-gray-700">PNS ({t('mentalHealth.detail.parasympatheticLabel')})</span>
                                <span className="font-bold text-blue-600">{report.psdData?.HFnu?.toFixed(1)}%</span>
                            </div>
                            <div className="bg-gray-200 rounded-full h-4">
                                <div className="bg-gradient-to-r from-blue-400 to-blue-600 h-4 rounded-full"
                                    style={{ width: `${report.psdData.HFnu}%` }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== 페이지 3: HRV 기반 증상 예측 ===== */}
            <div className="p-8">
                <h2 data-pdf-heading className="text-2xl font-bold border-l-4 border-indigo-600 pl-4 mb-6">🏥 {t('mentalHealth.detail.symptoms')}</h2>

                {report.symptomPredictions ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <SymptomPredictionCard prediction={report.symptomPredictions.stress} />
                            <SymptomPredictionCard prediction={report.symptomPredictions.breathing} />
                            <SymptomPredictionCard prediction={report.symptomPredictions.pain} />
                            <SymptomPredictionCard prediction={report.symptomPredictions.depression} />
                            <SymptomPredictionCard prediction={report.symptomPredictions.cardiovascular} />
                            <SymptomPredictionCard prediction={report.symptomPredictions.bloodPressure} />
                        </div>

                        {/* 증상별 설명 */}
                        <div data-pdf-block className="bg-gray-50 rounded-xl border border-gray-200 p-6 mt-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">📚 {t('mentalHealth.detail.symptomIndicatorGuide')}</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div className="border-l-3 border-blue-500 pl-3">
                                    <h4 className="font-bold text-gray-800 mb-1">🧠 {t('mentalHealth.symptom.stress')}</h4>
                                    <p className="text-gray-600">{t('mentalHealth.detail.stressIndicatorDesc')}</p>
                                </div>
                                <div className="border-l-3 border-cyan-500 pl-3">
                                    <h4 className="font-bold text-gray-800 mb-1">🫁 {t('mentalHealth.symptom.breathing')}</h4>
                                    <p className="text-gray-600">{t('mentalHealth.detail.breathingIndicatorDesc')}</p>
                                </div>
                                <div className="border-l-3 border-orange-500 pl-3">
                                    <h4 className="font-bold text-gray-800 mb-1">😣 {t('mentalHealth.symptom.pain')}</h4>
                                    <p className="text-gray-600">{t('mentalHealth.detail.painIndicatorDesc')}</p>
                                </div>
                                <div className="border-l-3 border-purple-500 pl-3">
                                    <h4 className="font-bold text-gray-800 mb-1">😔 {t('mentalHealth.symptom.depression')}</h4>
                                    <p className="text-gray-600">{t('mentalHealth.detail.depressionIndicatorDesc')}</p>
                                </div>
                                <div className="border-l-3 border-red-500 pl-3">
                                    <h4 className="font-bold text-gray-800 mb-1">❤️ {t('mentalHealth.symptom.cardiovascular')}</h4>
                                    <p className="text-gray-600">{t('mentalHealth.detail.cardiovascularIndicatorDesc')}</p>
                                </div>
                                <div className="border-l-3 border-pink-500 pl-3">
                                    <h4 className="font-bold text-gray-800 mb-1">💉 {t('mentalHealth.symptom.bloodPressure')}</h4>
                                    <p className="text-gray-600">{t('mentalHealth.detail.bloodPressureIndicatorDesc')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div data-pdf-block className="text-center p-8 bg-yellow-50 rounded-xl border border-yellow-200">
                        <div className="text-4xl mb-4">⚠️</div>
                        <h3 className="text-lg font-bold text-yellow-800 mb-2">{t('mentalHealth.detail.symptomPredictionNoData')}</h3>
                        <p className="text-yellow-700">{t('mentalHealth.detail.symptomPredictionOldReport')}</p>
                    </div>
                )}

                {/* 주의사항 */}
                <div data-pdf-block className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                    <p className="text-sm text-yellow-800" dangerouslySetInnerHTML={{ __html: t('mentalHealth.detail.symptomNote') }} />
                </div>
            </div>

            {/* ===== 페이지 4: 상세 차트 ===== */}
            {report.mindScore.selectedWindows && (
                <div className="p-8">
                    <h2 data-pdf-heading className="text-2xl font-bold border-l-4 border-purple-600 pl-4 mb-6">📈 {t('mentalHealth.detail.charts')}</h2>
                    <div className="grid grid-cols-2 gap-6">
                        <VitalSignsChart windows={report.mindScore.selectedWindows} type="hr" />
                        <VitalSignsChart windows={report.mindScore.selectedWindows} type="rmssd" />
                        <VitalSignsChart windows={report.mindScore.selectedWindows} type="resp" />
                        <VitalSignsChart windows={report.mindScore.selectedWindows} type="dqi" />
                    </div>

                    {/* 차트 설명 */}
                    <div data-pdf-block className="bg-gray-50 rounded-xl border border-gray-200 p-6 mt-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">📊 {t('mentalHealth.detail.hrvDetailedAnalysis')}</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="bg-white p-3 rounded-lg border border-gray-100">
                                <h4 className="font-semibold text-gray-800 mb-1">{t('mentalHealth.detail.heartRateChart')}</h4>
                                <p className="text-gray-600">{t('mentalHealth.detail.average')}: {report.mindScore.components?.HR_med?.toFixed(0)} bpm</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-100">
                                <h4 className="font-semibold text-gray-800 mb-1">{t('mentalHealth.detail.rmssdChart')}</h4>
                                <p className="text-gray-600">{t('mentalHealth.detail.average')}: {report.mindScore.components?.RMSSD_med?.toFixed(1)} ms</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-100">
                                <h4 className="font-semibold text-gray-800 mb-1">{t('mentalHealth.detail.respirationChart')}</h4>
                                <p className="text-gray-600">{t('mentalHealth.detail.average')}: {report.mindScore.components?.Resp_med?.toFixed(1)} bpm</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-100">
                                <h4 className="font-semibold text-gray-800 mb-1">{t('mentalHealth.detail.dqiChart')}</h4>
                                <p className="text-gray-600">{t('mentalHealth.detail.dataQualityLabel')}: {((report.qualityMetrics?.analyzability || 0) * 100).toFixed(0)}%</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== 페이지 5: 원시 데이터 ===== */}
            <div className="p-8">
                <h2 data-pdf-heading className="text-2xl font-bold border-l-4 border-gray-600 pl-4 mb-6">🔢 {t('mentalHealth.detail.rawData')}</h2>

                {/* HRV 상세 수치 테이블 */}
                <div data-pdf-block className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">{t('mentalHealth.detail.hrvDetailedAnalysis')}</h3>

                    <div className="grid grid-cols-2 gap-6">
                        {/* 시간 영역 분석 */}
                        <div>
                            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">{t('mentalHealth.detail.timeAnalysis')}</h4>
                            <table className="w-full text-sm">
                                <tbody>
                                    <tr className="border-b">
                                        <td className="py-2 text-gray-600">{t('mentalHealth.reportDetail.heartRate')}</td>
                                        <td className="py-2 font-medium text-right">{report.mindScore.components?.HR_med?.toFixed(1)} bpm</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2 text-gray-600">{t('mentalHealth.reportDetail.rmssd')}</td>
                                        <td className="py-2 font-medium text-right">{report.mindScore.components?.RMSSD_med?.toFixed(2)} ms</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2 text-gray-600">{t('mentalHealth.reportDetail.respiration')}</td>
                                        <td className="py-2 font-medium text-right">{report.mindScore.components?.Resp_med?.toFixed(1)} bpm</td>
                                    </tr>
                                    <tr>
                                        <td className="py-2 text-gray-600">{t('mentalHealth.reportDetail.duration')}</td>
                                        <td className="py-2 font-medium text-right">{report.qualityMetrics?.duration_min?.toFixed(1)} {t('mentalHealth.reportDetail.minutes')}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* 주파수 영역 분석 */}
                        <div>
                            <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2">{t('mentalHealth.detail.frequencyAnalysis')}</h4>
                            <table className="w-full text-sm">
                                <tbody>
                                    <tr className="border-b">
                                        <td className="py-2 text-gray-600">{t('mentalHealth.detail.totalPower')} (TP)</td>
                                        <td className="py-2 font-medium text-right">{report.psdData?.TP?.toFixed(2)} ms²</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2 text-gray-600">VLF</td>
                                        <td className="py-2 font-medium text-right">{report.psdData?.VLF?.toFixed(2)} ms²</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2 text-gray-600">LF</td>
                                        <td className="py-2 font-medium text-right">{report.psdData?.LF?.toFixed(2)} ms²</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2 text-gray-600">HF</td>
                                        <td className="py-2 font-medium text-right">{report.psdData?.HF?.toFixed(2)} ms²</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2 text-gray-600">{t('mentalHealth.detail.lfhfRatio')}</td>
                                        <td className="py-2 font-medium text-right">{report.psdData?.LF_HF?.toFixed(3)}</td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2 text-gray-600">LFnu</td>
                                        <td className="py-2 font-medium text-right">{report.psdData?.LFnu?.toFixed(2)}%</td>
                                    </tr>
                                    <tr>
                                        <td className="py-2 text-gray-600">HFnu</td>
                                        <td className="py-2 font-medium text-right">{report.psdData?.HFnu?.toFixed(2)}%</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* 점수 구성요소 */}
                {report.mindScore.components && (
                    <div data-pdf-block className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">{t('mentalHealth.reportDetail.mindScoreDetail')}</h3>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-gray-50">
                                    <th className="py-2 px-3 text-left text-gray-600">{t('mentalHealth.reportDetail.score')} {t('common.indicators')}</th>
                                    <th className="py-2 px-3 text-right text-gray-600">{t('mentalHealth.detail.average')}</th>
                                    <th className="py-2 px-3 text-right text-gray-600">{t('mentalHealth.reportDetail.score')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b">
                                    <td className="py-2 px-3">{t('mentalHealth.reportDetail.heartRate')}</td>
                                    <td className="py-2 px-3 text-right">{report.mindScore.components?.HR_med?.toFixed(1)} bpm</td>
                                    <td className="py-2 px-3 text-right font-medium">{report.mindScore.components?.s_HR?.toFixed(1)}</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2 px-3">{t('mentalHealth.reportDetail.rmssd')}</td>
                                    <td className="py-2 px-3 text-right">{report.mindScore.components?.RMSSD_med?.toFixed(1)} ms</td>
                                    <td className="py-2 px-3 text-right font-medium">{report.mindScore.components?.s_RMSSD?.toFixed(1)}</td>
                                </tr>
                                <tr className="border-b">
                                    <td className="py-2 px-3">{t('mentalHealth.reportDetail.respiration')}</td>
                                    <td className="py-2 px-3 text-right">{report.mindScore.components?.Resp_med?.toFixed(1)} bpm</td>
                                    <td className="py-2 px-3 text-right font-medium">{report.mindScore.components?.s_Resp?.toFixed(1)}</td>
                                </tr>
                                <tr className="bg-blue-50">
                                    <td className="py-2 px-3 font-bold">{t('mentalHealth.detail.mindHealthScore')}</td>
                                    <td className="py-2 px-3 text-right">-</td>
                                    <td className="py-2 px-3 text-right font-bold text-blue-600">{report.mindScore.score?.toFixed(1)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {/* JSON Raw Data (축약) */}
                <div data-pdf-block className="bg-gray-50 rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">📄 JSON {t('mentalHealth.detail.rawData')}</h3>
                    <pre className="bg-white p-4 rounded border border-gray-200 text-[10px] leading-relaxed whitespace-pre-wrap break-all">
                        {JSON.stringify({
                            mindScore: {
                                ...report.mindScore,
                                selectedWindows: undefined,
                                windowCount: report.mindScore.selectedWindows?.length ?? 0
                            },
                            stressAnalysis: report.stressAnalysis,
                            balanceAnalysis: report.balanceAnalysis,
                            psdData: report.psdData,
                            qualityMetrics: report.qualityMetrics,
                            respirationState: report.respirationState,
                            decisionLabel: report.decisionLabel
                        }, null, 2)}
                    </pre>
                </div>

                {/* Footer */}
                <div data-pdf-block className="text-center text-xs text-gray-400 pt-8 border-t mt-8">
                    Generated by Carearly - {new Date().toLocaleString(intlLocale)}
                </div>
            </div>
        </div>
    );
};

export default MentalHealthReportDetailedPDFTemplate;
